import React from "react";
import { ChangeEvent, FormEvent, Fragment } from "react";
import ReactModal from "react-modal";
import { RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";

import { DateTime } from "luxon";

import Checkbox from "../Checkbox";
import HasRight from "../HasRight";
import NoteBox from "../NoteBox";
import OverflowMenuItem from "../OverflowMenuItem";
import ToolbarMenuItem from "../ToolbarMenuItem";
import ViewToolbar from "../ViewToolbar";

import { V2, apiGet } from "../../Api";
import { formatDateLocal, formatDateLocalDayOfWeek, formatName, formatTelephone, formatTimeLocal } from "../../Utilities";
import { raceToString, surgeryStatusToText } from "../../TextTables";
import { IAuthProps } from "../AuthContext";
import withAuth from "../withAuth";
import withTitle from "../withTitle";

import "../../blocks.css";
import "../../form.css";
import "../../icomoon.css";
import "../viewer.css";

interface ISurgeryAppointmentViewState {
    dialogSubmitStatus: boolean | string;
    isLoading: boolean;
    isModalOpen: boolean;
    loadError?: string;
    hasNoteChanged: boolean;
    modalMode: "" | "cancel" | "softcancel" | "activate" | "notneeded" | "failed";

    surgeries: any[];

    surgery?: any;

    dialogReason: string;
    dialogTime: string;

    hideMarketingNoteControls: boolean;
    hidePostNoteControls: boolean;
    hidePreNoteControls: boolean;
    hideInsuranceNoteControls: boolean;
    hideContactNoteControls: boolean;
    allowNoteUploads: boolean;

    isDeletionModalOpen: boolean;
    deletionModalLoading: boolean | string;

    isLinkModalOpen: boolean;
    dialogLinkId: string;

    contactModalLoading: boolean;
    isContactModalOpen: boolean;

    contactNote: string;
    contactFinal: boolean;

    reviewModalLoading: boolean;
    isReviewModalOpen: boolean;
    reviewNote: string;
    reviewNoteExists: boolean;
    reviewFiles: string[];

    reviewChecklistBmi: boolean;
    reviewChecklistBmiValue: string;
    reviewChecklistHgb: boolean;
    reviewChecklistHgbValue: string;
    reviewChecklistA1c: boolean;
    reviewChecklistA1cValue: string;
    reviewChecklistPap: boolean;
    reviewChecklistPapValue: string;
    reviewChecklistBp: boolean;
    reviewChecklistBpValue: string;
    reviewChecklistLabs: boolean;
    reviewChecklistLabsValue: string;
    reviewChecklistVitals: boolean;
    reviewChecklistVitalsValue: string;
    reviewChecklistRx: boolean;
    reviewChecklistRxValue: string;
    reviewChecklistAllergies: boolean;
    reviewChecklistAllergiesValue: string;
    reviewChecklistHealthHistory: boolean;
    reviewChecklistHealthHistoryValue: string;
    reviewIsFinal: boolean;

    history: any[];

    employees: any[];

    cancelReasons: any[];
    selectedCancelReason: string;

    // [name: string]: any;
}

class SurgeryAppointmentView extends React.Component<RouteComponentProps<any> & IAuthProps, ISurgeryAppointmentViewState> {
    private reviewNoteRef: React.RefObject<NoteBox> = React.createRef();

    constructor(props: Readonly<RouteComponentProps<any> & IAuthProps>) {
        super(props);

        const state: ISurgeryAppointmentViewState = {
            dialogSubmitStatus: false,
            isLoading: true,
            isModalOpen: false,
            hasNoteChanged: false,
            modalMode: "",

            surgeries: [],

            dialogReason: "",
            dialogTime: "",

            hideMarketingNoteControls: !props.authContext.hasRight("surgery:note:marketing"),
            hidePostNoteControls: !props.authContext.hasRight("surgery:note:post"),
            hidePreNoteControls: !props.authContext.hasRight("surgery:note:pre"),
            hideInsuranceNoteControls: !props.authContext.hasRight("surgery:note:ins"),
            hideContactNoteControls: !props.authContext.hasRight("surgery:note:contact"),
            allowNoteUploads: props.authContext.hasRight("surgery:note:upload"),

            deletionModalLoading: false,
            isDeletionModalOpen: false,

            isLinkModalOpen: false,
            dialogLinkId: "",

            history: [],

            employees: [],

            contactModalLoading: false,
            isContactModalOpen: false,

            contactNote: "",
            contactFinal: false,

            reviewModalLoading: false,
            isReviewModalOpen: false,
            reviewNote: "",
            reviewNoteExists: false,
            reviewFiles: [],

            reviewChecklistBmi: false,
            reviewChecklistBmiValue: "",
            reviewChecklistHgb: false,
            reviewChecklistHgbValue: "",
            reviewChecklistA1c: false,
            reviewChecklistA1cValue: "",
            reviewChecklistPap: false,
            reviewChecklistPapValue: "",
            reviewChecklistBp: false,
            reviewChecklistBpValue: "",
            reviewChecklistLabs: false,
            reviewChecklistLabsValue: "",
            reviewChecklistVitals: false,
            reviewChecklistVitalsValue: "",
            reviewChecklistRx: false,
            reviewChecklistRxValue: "",
            reviewChecklistAllergies: false,
            reviewChecklistAllergiesValue: "",
            reviewChecklistHealthHistory: false,
            reviewChecklistHealthHistoryValue: "",
            reviewIsFinal: false,

            cancelReasons: [],
            selectedCancelReason: "",
        };

        this.state = state;

        this.doMarkActive = this.doMarkActive.bind(this);
        this.doRealMarkActive = this.doRealMarkActive.bind(this);
        this.doMarkNoShow = this.doMarkNoShow.bind(this);
        this.doCancelNotNeeded = this.doCancelNotNeeded.bind(this);
        this.doCancel = this.doCancel.bind(this);
        this.doOperationFailure = this.doOperationFailure.bind(this);
        this.doRealCancel = this.doRealCancel.bind(this);
        this.doRealSoftCancel = this.doRealSoftCancel.bind(this);
        this.doRealNotNeeded = this.doRealNotNeeded.bind(this);
        this.doRealFailure = this.doRealFailure.bind(this);
        this.doResetToActive = this.doResetToActive.bind(this);
        this.doResetToPending = this.doResetToPending.bind(this);
        this.handlePreNote = this.handlePreNote.bind(this);
        this.handlePostNote = this.handlePostNote.bind(this);
        this.handleMarketingNote = this.handleMarketingNote.bind(this);
        this.handleInsuranceNote = this.handleInsuranceNote.bind(this);
        this.handleContactNote = this.handleContactNote.bind(this);
        this.handlePreopNoteDelete = this.handlePreopNoteDelete.bind(this);
        this.handlePostopNoteDelete = this.handlePostopNoteDelete.bind(this);
        this.handleMarketingNoteDelete = this.handleMarketingNoteDelete.bind(this);
        this.handleInsuranceNoteDelete = this.handleInsuranceNoteDelete.bind(this);
        this.handleContactNoteDelete = this.handleContactNoteDelete.bind(this);

        this.closeDialog = this.closeDialog.bind(this);
        this.dialogInputChanged = this.dialogInputChanged.bind(this);
        this.cancelDialogReasonChanged = this.cancelDialogReasonChanged.bind(this);

        this.deleteItem = this.deleteItem.bind(this);
        this.realDeleteItem = this.realDeleteItem.bind(this);
        this.closeDeletionModal = this.closeDeletionModal.bind(this);

        this.linkAppointment = this.linkAppointment.bind(this);
        this.closeLinkModal = this.closeLinkModal.bind(this);
        this.realLinkAppointment = this.realLinkAppointment.bind(this);

        this.openContactModal = this.openContactModal.bind(this);
        this.closeContactModal = this.closeContactModal.bind(this);

        this.openReviewModal = this.openReviewModal.bind(this);
        this.closeReviewModal = this.closeReviewModal.bind(this);
        this.handleReviewNote = this.handleReviewNote.bind(this);
        this.handleReviewNoteChange = this.handleReviewNoteChange.bind(this);

        this.openLabelSheetWindow = this.openLabelSheetWindow.bind(this);
        this.handleMessage = this.handleMessage.bind(this);
    }

    public async componentDidMount() {
        window.addEventListener("message", this.handleMessage, false);

        try {
            const surgeries = await V2.get<any>("glossary/surgeries");

            let employees = [];
            if (this.props.authContext.hasRight("surgery:history")) {
                employees = await apiGet<any[]>("employees/summary?limit=400");
            }

            let cancelReasons = [];
            try {
                const listDescriptor = (await V2.get<any>(`list/resolve?locator=surgery-cancel-reason`))?.lists?.[0];
                if (!listDescriptor) {
                    throw new Error("Invalid data list returned");
                }

                const list = await V2.get<any>(`list/type/${listDescriptor.type}/${listDescriptor.id}`);
                cancelReasons = list.items.map(a => {
                    let [_, title, value] = a.value.match(/\[\[(.+)\]\]\s?(.+)$/s);

                    // Support newlines in single-line datalist format
                    value = value.split("\\n").join("\n");

                    return {
                        title: title || "",
                        value: value || "",
                    };
                });
            }
            catch (e) {
                // No datalist found
            }

            // Add default placeholder reason
            cancelReasons.unshift({
                title: "Reason entered below",
                value: "",
            });

            this.setState({
                surgeries,
                employees,
                cancelReasons
            });

            this.loadSurgery();
        }
        catch (e) {
            this.setState({
                loadError: e.message
            });
        }
    }

    public componentWillUnmount() {
        window.removeEventListener("message", this.handleMessage);
    }

    public render() {
        if (this.state.loadError) {
            return <div className="viewer-root">
                <div className="error-box">
                    Error loading surgery appointment:<br />
                    <br />
                    {this.state.loadError}
                </div>
            </div>
        }

        if (this.state.isLoading) {
            return <div className="spinner spinner-full-root lds-dual-ring-big" />;
        }

        return (
            <div className="viewer-root">
                <ViewToolbar>
                    <ToolbarMenuItem right="surgery:doc:read">
                        <Link to={{ pathname: this.props.location.pathname + "/documents" }} className="icon-file-text-o" title="View Documents" />
                    </ToolbarMenuItem>
                    <ToolbarMenuItem right={this.state.surgery.status > 1 ? "surgery:write:admin" : "surgery:write"}>
                        <Link to={{ pathname: this.props.location.pathname + "/edit", state: { surgery: this.getStrippedSurgery() } }} className="icon-pencil" title="Edit" />
                    </ToolbarMenuItem>
                    <OverflowMenuItem>
                        <ToolbarMenuItem right="surgery:cancel" condition={this.state.surgery.status === 0 || this.state.surgery.status === 1}>
                            <button type="button" onClick={this.doMarkNoShow}><i className="icon-times" /><span>Soft-cancel Appointment</span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem right="surgery:cancel" condition={this.state.surgery.status === 0 || this.state.surgery.status === 1}>
                            <button type="button" onClick={this.doCancel}><i className="icon-calendar-times-o" /><span>Hard-cancel Appointment</span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem right="surgery:cancel" condition={this.state.surgery.officeVisitType === "telemed" && (this.state.surgery.status === 0 || this.state.surgery.status === 1)}>
                            <button type="button" onClick={this.doCancelNotNeeded}><i className="icon-calendar-times-o" /><span>Surgery Not Needed</span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem condition={this.state.surgery.status === 1}>
                            <Link to={{ pathname: this.props.location.pathname + "/edit", state: { surgery: this.getStrippedSurgery(), completeFlag: true } }}><i className="icon-check" /><span>Complete Appointment</span></Link>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem condition={this.state.surgery.status === 0}>
                            <button type="button" onClick={this.doMarkActive}><i className="icon-check" /><span>Mark as Active</span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem right="surgery:s:reset" condition={this.state.surgery.status !== 1 && this.state.surgery.status !== 0}>
                            <button type="button" onClick={this.doResetToActive}><i className="icon-check" /><span>Reset to active</span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem right="surgery:s:reset" condition={this.state.surgery.status !== 0}>
                            <button type="button" onClick={this.doResetToPending}><i className="icon-clock-o" /><span>Reset to pending</span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem condition={this.state.surgery.status === 1}>
                            <button type="button" onClick={this.doOperationFailure}><i className="icon-ban" /><span>Mark operation as Failed</span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem right="surgery:delete">
                            <button type="button" onClick={this.deleteItem}><i className="icon-trash" /><span>Delete surgery</span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem right="surgery:write:admin" condition={!this.state.surgery.officeAppointmentId}>
                            <button type="button" onClick={this.linkAppointment}><i className="icon-link" /><span>Link to office appointment</span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem right="surgery:contact">
                            <button type="button" onClick={this.openContactModal}><i className="icon-clock-o" /><span>Contacted</span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem right="surgery:review">
                            <button type="button" onClick={this.openReviewModal}><i className="icon-check-circle-o" /><span>Review</span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem right="document:edit">
                            <button type="button" onClick={this.openLabelSheetWindow}><i className="icon-print" /><span>Print Labels</span></button>
                        </ToolbarMenuItem>
                    </OverflowMenuItem>
                </ViewToolbar>
                {this.state.isModalOpen
                    ? <ReactModal
                        isOpen={this.state.isModalOpen} onRequestClose={this.closeDialog}
                        overlayClassName="modal-overlay" className="modal-content" portalClassName="modal-portal"
                        bodyOpenClassName="modal-open"
                        contentLabel="Confirm Appointment Info">
                        {this.state.modalMode === "activate"
                            ? <form className="input-form" onSubmit={this.doRealMarkActive}>
                                <section className="info-block">
                                    <header>
                                        <h3>Confirm Appointment Info</h3>
                                    </header>

                                    <span>Confirm the appointment time below</span>
                                    <br />
                                    <section>
                                        <div className="input-form-group">
                                            <label htmlFor="frm-dialog-date">Appointment Date</label>
                                            <span id="frm-dialog-date">{formatDateLocalDayOfWeek(this.state.surgery.date)}</span>
                                        </div>
                                    </section>
                                    <section>
                                        <div className="input-form-group">
                                            <label htmlFor="frm-dialog-time">Appointment Time</label>
                                            <input type="time" id="frm-dialog-time" name="dialogTime" value={this.state.dialogTime} onChange={this.dialogInputChanged} autoComplete="off_please_chrome" />
                                        </div>
                                    </section>
                                    <br />
                                    {
                                        this.state.dialogSubmitStatus === true
                                            ? <Fragment><div className="spinner lds-dual-ring" /><br /></Fragment>
                                            : typeof this.state.dialogSubmitStatus === "string"
                                                ? <Fragment><span className="error-text">{this.state.dialogSubmitStatus}</span><br /></Fragment>
                                                : ""
                                    }
                                    <div className="action-panel-dialog">
                                        <button type="submit" className="button green push-right" disabled={!this.state.dialogTime}>Mark Active</button>
                                        <button type="button" className="button" onClick={this.closeDialog}>Cancel</button>
                                    </div>
                                </section>
                            </form>
                            : <form className="input-form" onSubmit={this.modalModeToSubmit(this.state.modalMode)}>
                                <section className="info-block">
                                    <header>
                                        <h3>{this.modalModeToTitle(this.state.modalMode)}</h3>
                                    </header>

                                    <span>{this.modalModeToPhrase(this.state.modalMode)}</span>
                                    <br />
                                    <section>
                                        <div className="input-form-group">
                                            <label htmlFor="selectedCancelReason">Reason</label>
                                            <select name="selectedCancelReason" value={this.state.selectedCancelReason} onChange={this.cancelDialogReasonChanged}>
                                                {this.state.cancelReasons.map(a => <option key={a.title} value={a.title}>{a.title}</option>)}
                                            </select>
                                        </div>
                                    </section>
                                    <section>
                                        <div className="input-form-group">
                                            <textarea id="frm-dialog-reason" className="input-textarea-medium" name="dialogReason" value={this.state.dialogReason} onChange={this.dialogInputChanged} autoComplete="off_please_chrome" />
                                        </div>
                                    </section>
                                    <br />
                                    {
                                        this.state.dialogSubmitStatus === true
                                            ? <Fragment><div className="spinner lds-dual-ring" /><br /></Fragment>
                                            : typeof this.state.dialogSubmitStatus === "string"
                                                ? <Fragment><span className="error-text">{this.state.dialogSubmitStatus}</span><br /></Fragment>
                                                : ""
                                    }
                                    <div className="action-panel-dialog">
                                        <button type="submit" className="button red push-right" disabled={!this.state.dialogReason}>{this.state.modalMode === "failed" ? "Submit" : "Cancel Appointment"}</button>
                                        <button type="button" className="button" onClick={this.closeDialog}>Go Back</button>
                                    </div>
                                </section>
                            </form>
                        }
                    </ReactModal>
                    : null}
                <ReactModal
                    isOpen={this.state.isDeletionModalOpen} onRequestClose={this.closeDeletionModal}
                    overlayClassName="modal-overlay" className="modal-content" portalClassName="modal-deletion-portal"
                    bodyOpenClassName="modal-deletion-open"
                    contentLabel="Confirm Delete">
                    <form className="input-form" onSubmit={this.realDeleteItem}>
                        <section className="info-block">
                            <header>
                                <h3>Confirm Delete</h3>
                            </header>

                            <span>Are you sure you want to <em>permanently</em> delete this surgery and all directly-associated information?</span>
                            <p>{formatName(this.state.surgery.patient.name)}<br /><small>{formatDateLocal(this.state.surgery.date)}</small></p>
                            <p><b>This action cannot be undone.</b></p>
                            {(this.state.surgery.preSurgeryNotes?.length ||
                                this.state.surgery.postSurgeryNotes?.length ||
                                this.state.surgery.marketingNotes?.length ||
                                this.state.surgery.insuranceNotes?.length ||
                                this.state.surgery.contactNotes?.length)
                                ? <div className="warning-box">
                                    <b>Warning:</b> This surgery has the following kinds of notes on it.<br />
                                    <br />
                                    <ul className="bullet-list">
                                        {this.state.surgery.preSurgeryNotes?.length ? <li>Pre-op notes</li> : null}
                                        {this.state.surgery.postSurgeryNotes?.length ? <li>Post-op notes</li> : null}
                                        {this.state.surgery.marketingNotes?.length ? <li>Marketing notes</li> : null}
                                        {this.state.surgery.insuranceNotes?.length ? <li><b>Insurance notes</b></li> : null}
                                        {this.state.surgery.contactNotes?.length ? <li>Contact notes</li> : null}
                                    </ul>
                                    <br />
                                    If you delete this surgery, the notes will be deleted with it.<br />
                                    <p>
                                        <b>Make sure you do not need the notes before deleting!</b>
                                    </p>
                                </div>
                                : null}
                            {
                                this.state.deletionModalLoading === true
                                    ? <div className="spinner lds-dual-ring" />
                                    : (typeof this.state.deletionModalLoading) === "string"
                                        ? <span className="error-text">{this.state.deletionModalLoading}</span>
                                        : ""
                            }
                            &nbsp;
                            <div className="action-panel-dialog">
                                <button type="submit" className="button red push-right">Yes, Delete</button>
                                <button type="button" className="button" onClick={this.closeDeletionModal}>No</button>
                            </div>
                        </section>
                    </form>
                </ReactModal>
                {this.state.isLinkModalOpen
                    ? <ReactModal
                        isOpen={this.state.isLinkModalOpen} onRequestClose={this.closeLinkModal}
                        overlayClassName="modal-overlay" className="modal-content" portalClassName="modal-portal"
                        bodyOpenClassName="modal-open"
                        contentLabel="Confirm Delete">
                        <form className="input-form" onSubmit={this.realLinkAppointment}>
                            <section className="info-block">
                                <header>
                                    <h3>Link appointment</h3>
                                </header>

                                <span>Enter the ID number of the appointment to link</span>
                                &nbsp;
                                <section>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-dialog-id">Appointment ID</label>
                                        <input type="text" className="input-center" id="frm-dialog-id" maxLength={20} name="dialogLinkId" value={this.state.dialogLinkId} onChange={this.dialogInputChanged} />
                                    </div>
                                </section>
                                {
                                    this.state.dialogSubmitStatus === true
                                        ? <Fragment><div className="spinner lds-dual-ring" /><br /></Fragment>
                                        : typeof this.state.dialogSubmitStatus === "string"
                                            ? <Fragment><span className="error-text">{this.state.dialogSubmitStatus}</span><br /></Fragment>
                                            : ""
                                }
                                &nbsp;
                            <div className="action-panel-dialog">
                                    <button type="submit" className="button push-right text-with-icon"><i className="icon-link" /><span>Link</span></button>
                                    <button type="button" className="button" onClick={this.closeLinkModal}>Cancel</button>
                                </div>
                            </section>
                        </form>
                    </ReactModal>
                    : null}
                {this.state.isContactModalOpen
                    ? <ReactModal
                        isOpen={this.state.isContactModalOpen} onRequestClose={this.closeContactModal}
                        overlayClassName="modal-overlay" className="modal-content modal-larger" portalClassName="modal-billing-portal"
                        bodyOpenClassName="modal-billing-open"
                        contentLabel="Contacted dialog">
                        <form className="input-form">
                            <section className="info-block">
                                <header>
                                    <h3>Contacted patient</h3>
                                </header>

                                <section>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-contact-note">Note <small>(optional)</small></label>
                                        <textarea id="frm-contact-note" className="input-textarea-medium" name="contactNote" value={this.state.contactNote} onChange={this.dialogInputChanged} autoComplete="off_please_chrome" />
                                    </div>
                                </section>
                                <section>
                                    <div className="input-form-group">
                                        <div className="input-form-group-check-inline">
                                            <Checkbox fancy={true} id="frm-contact-final" name="contactFinal" checked={this.state.contactFinal} onChange={this.dialogInputChanged} autoComplete="off_please_chrome">Is final contact</Checkbox>
                                        </div>
                                    </div>
                                </section>
                                {
                                    this.state.contactModalLoading === true
                                        ? <div className="spinner lds-dual-ring" />
                                        : (typeof this.state.contactModalLoading) === "string"
                                            ? <span className="error-text">{this.state.contactModalLoading}</span>
                                            : ""
                                }
                                &nbsp;
                                <div className="action-panel-dialog">
                                    <button type="button" className="button green" onClick={() => this.contactStatus("will")}>Will come</button>
                                    <button type="button" className="button yellow" onClick={() => this.contactStatus("might")}>Might come</button>
                                    <button type="button" className="button red" onClick={() => this.contactStatus("not")}>Not coming</button>
                                    <button type="button" className="button" onClick={this.closeContactModal}>Close</button>
                                </div>
                            </section>
                        </form>
                    </ReactModal>
                    : null}
                {this.state.isReviewModalOpen
                    ? <ReactModal
                        isOpen={this.state.isReviewModalOpen} onRequestClose={this.closeReviewModal}
                        overlayClassName="modal-overlay" className="modal-content modal-larger" portalClassName="modal-billing-portal"
                        bodyOpenClassName="modal-billing-open"
                        contentLabel="Review dialog">
                        <form className="input-form">
                            <section className="info-block">
                                <header>
                                    <h3>Reviewed surgery</h3>
                                </header>

                                <section>
                                    <div className="input-form-group-h">
                                        <Checkbox fancy={true} id="reviewChecklistBmi" name="reviewChecklistBmi" checked={this.state.reviewChecklistBmi} onChange={this.dialogInputChanged}>BMI</Checkbox>
                                        <input type="text" className="input-light" name="reviewChecklistBmiValue" value={this.state.reviewChecklistBmiValue} onChange={this.dialogInputChanged} />
                                    </div>
                                    <div className="input-form-group-h">
                                        <Checkbox fancy={true} id="reviewChecklistHgb" name="reviewChecklistHgb" checked={this.state.reviewChecklistHgb} onChange={this.dialogInputChanged}>HGB</Checkbox>
                                        <input type="text" className="input-light" name="reviewChecklistHgbValue" value={this.state.reviewChecklistHgbValue} onChange={this.dialogInputChanged} />
                                    </div>
                                </section>
                                <section>
                                    <div className="input-form-group-h">
                                        <Checkbox fancy={true} id="reviewChecklistA1c" name="reviewChecklistA1c" checked={this.state.reviewChecklistA1c} onChange={this.dialogInputChanged}>A1C</Checkbox>
                                        <input type="text" className="input-light" name="reviewChecklistA1cValue" value={this.state.reviewChecklistA1cValue} onChange={this.dialogInputChanged} />
                                    </div>
                                    <div className="input-form-group-h">
                                        <Checkbox fancy={true} id="reviewChecklistPap" name="reviewChecklistPap" checked={this.state.reviewChecklistPap} onChange={this.dialogInputChanged}>PAP</Checkbox>
                                        <input type="text" className="input-light" name="reviewChecklistPapValue" value={this.state.reviewChecklistPapValue} onChange={this.dialogInputChanged} />
                                    </div>
                                </section>
                                <section>
                                    <div className="input-form-group-h">
                                        <Checkbox fancy={true} id="reviewChecklistBp" name="reviewChecklistBp" checked={this.state.reviewChecklistBp} onChange={this.dialogInputChanged}>BP</Checkbox>
                                        <input type="text" className="input-light" name="reviewChecklistBpValue" value={this.state.reviewChecklistBpValue} onChange={this.dialogInputChanged} />
                                    </div>
                                    <div className="input-form-group-h">
                                        <Checkbox fancy={true} id="reviewChecklistLabs" name="reviewChecklistLabs" checked={this.state.reviewChecklistLabs} onChange={this.dialogInputChanged}>Labs</Checkbox>
                                        <input type="text" className="input-light" name="reviewChecklistLabsValue" value={this.state.reviewChecklistLabsValue} onChange={this.dialogInputChanged} />
                                    </div>
                                </section>
                                <section>
                                    <div className="input-form-group-h">
                                        <Checkbox fancy={true} id="reviewChecklistVitals" name="reviewChecklistVitals" checked={this.state.reviewChecklistVitals} onChange={this.dialogInputChanged}>Vitals</Checkbox>
                                        <input type="text" className="input-light" name="reviewChecklistVitalsValue" value={this.state.reviewChecklistVitalsValue} onChange={this.dialogInputChanged} />
                                    </div>
                                    <div className="input-form-group-h">
                                        <Checkbox fancy={true} id="reviewChecklistRx" name="reviewChecklistRx" checked={this.state.reviewChecklistRx} onChange={this.dialogInputChanged}>RX</Checkbox>
                                        <input type="text" className="input-light" name="reviewChecklistRxValue" value={this.state.reviewChecklistRxValue} onChange={this.dialogInputChanged} />
                                    </div>
                                </section>
                                <section>
                                    <div className="input-form-group-h">
                                        <Checkbox fancy={true} id="reviewChecklistAllergies" name="reviewChecklistAllergies" checked={this.state.reviewChecklistAllergies} onChange={this.dialogInputChanged}>Allergies</Checkbox>
                                        <input type="text" className="input-light" name="reviewChecklistAllergiesValue" value={this.state.reviewChecklistAllergiesValue} onChange={this.dialogInputChanged} />
                                    </div>
                                </section>
                                <section>
                                    <div className="input-form-group-h">
                                        <Checkbox fancy={true} id="reviewChecklistHealthHistory" name="reviewChecklistHealthHistory" checked={this.state.reviewChecklistHealthHistory} onChange={this.dialogInputChanged}>Health History</Checkbox>
                                    </div>
                                </section>
                                <section>
                                    <div className="input-form-group">
                                        <label>Note <small>(required if there's an issue)</small></label>
                                        <NoteBox ref={this.reviewNoteRef} editorClassName="input-textarea-medium" defaultNoteOpen={true} stayOpenAfterSubmit={true} allowUpload={true} hideLoading={true} mode="single" onNewNote={this.handleReviewNote} onNewNoteChange={this.handleReviewNoteChange} />
                                    </div>
                                </section>
                                <section>
                                    <div className="input-form-group-h">
                                        <Checkbox fancy={true} id="reviewIsFinal" name="reviewIsFinal" checked={this.state.reviewIsFinal} onChange={this.dialogInputChanged}>Is Final Review</Checkbox>
                                    </div>
                                </section>
                                {
                                    this.state.reviewModalLoading === true
                                        ? <div className="spinner lds-dual-ring" />
                                        : (typeof this.state.reviewModalLoading) === "string"
                                            ? <span className="error-text">{this.state.reviewModalLoading}</span>
                                            : ""
                                }
                                &nbsp;
                                <div className="action-panel-dialog">
                                    <span className="spring" />
                                    <button type="button" className="button green" onClick={() => this.reviewStatus("goodtogo")}>Good to go</button>
                                    <button type="button" className="button yellow" onClick={() => this.reviewStatus("issue")} disabled={!this.state.reviewNoteExists}>There's an issue</button>
                                    <button type="button" className="button" onClick={this.closeReviewModal}>Close</button>
                                </div>
                            </section>
                        </form>
                    </ReactModal>
                    : null}
                <div className="column-root">
                    <section className="info-block">
                        <header>
                            <h3>Patient Information</h3>
                        </header>
                        <dl className="info-list">
                            <div className="info-list-row">
                                <div>
                                    <dt>Name</dt>
                                    <dd>{formatName(this.state.surgery.patient.name)}</dd>
                                </div>
                                <div>
                                    <dt>Date of Birth</dt>
                                    <dd>{formatDateLocal(this.state.surgery.patient.birthDate)}</dd>
                                </div>
                            </div>

                            <div className="info-list-row">
                                <div>
                                    <dt>Phone Number</dt>
                                    {
                                        this.state.surgery.patient.phone
                                            ? <dd><a href={`tel:${this.state.surgery.patient.phone}`}>{formatTelephone(this.state.surgery.patient.phone)}</a></dd>
                                            : <dd>N/A</dd>
                                    }
                                </div>
                                <div>
                                    <dt>Email Address</dt>
                                    {
                                        this.state.surgery.patient.email
                                            ? <dd><a href={`mailto:${this.state.surgery.patient.email}`} rel="noopener noreferrer" target="_blank">{this.state.surgery.patient.email}</a></dd>
                                            : <dd>N/A</dd>
                                    }
                                </div>
                            </div>

                            <dt>Address</dt>
                            <dd>{this.state.surgery.patient.address.street}</dd>
                            <dd>{this.state.surgery.patient.address.city}, {this.state.surgery.patient.address.state} {this.state.surgery.patient.address.zip}</dd>

                            <div className="info-list-row">
                                <div>
                                    <dt>Sex</dt>
                                    <dd>{this.sexToString(this.state.surgery.patient.sex)}</dd>
                                </div>
                                <div>
                                    <dt>Race</dt>
                                    <dd>{raceToString(this.state.surgery.patient.race)}</dd>
                                </div>
                            </div>

                            <div className="info-list-row">
                                <div>
                                    <dt>Insurance Provider</dt>
                                    <dd>{this.state.surgery.patient.insuranceProvider}</dd>
                                </div>
                                <div>
                                    <dt>Chart #</dt>
                                    <dd>{this.state.surgery.patient.chartNumber}</dd>
                                </div>
                            </div>

                            <dt>Source</dt>
                            <dd>
                                {
                                    this.state.surgery.officeAppointmentId
                                        ? <Fragment>Office appointment <HasRight right="appt:read">&nbsp;<Link to={`/appointments/${this.state.surgery.officeAppointmentId}`} className="button small btn-narrow btn-inline btn-outline">View</Link></HasRight></Fragment>
                                        : "Standalone"
                                }
                            </dd>
                        </dl>
                    </section>
                    <section className="info-block">
                        <header>
                            <h3>Appointment Information</h3>
                        </header>

                        <dl className="info-list">
                            <dt>Location</dt>
                            <dd>{this.state.surgery.location.name}</dd>
                            <dd>
                                <small>
                                    {this.state.surgery.location.address.street} {`${this.state.surgery.location.address.city}, ${this.state.surgery.location.address.state} ${this.state.surgery.location.address.zip}`}
                                </small>
                            </dd>

                            <div className="info-list-row">
                                <div>
                                    <dt>Date</dt>
                                    <dd>{this.state.surgery.time ? formatDateLocal(this.state.surgery.timestamp) : formatDateLocal(this.state.surgery.date.substring(0, 10))}</dd>
                                </div>
                                <div>
                                    <dt>Time</dt>
                                    <dd>{this.state.surgery.time ? formatTimeLocal(this.state.surgery.timestamp) : "Not specified"}</dd>
                                </div>
                            </div>
                            <div className="info-list-row">
                                <div>
                                    <dt>Appointment Type</dt>
                                    <dd>{this.appointmentTypeToString(this.state.surgery.appointmentType)}</dd>
                                </div>
                                <div>
                                    <dt>Status</dt>
                                    <dd>{surgeryStatusToText(this.state.surgery.status)}</dd>
                                </div>
                            </div>
                            <div className="info-list-row">
                                <div>
                                    <dt>Provider</dt>
                                    <dd>{(this.state.surgery.provider && this.state.surgery.provider.name && formatName(this.state.surgery.provider.name)) || "Not Specified"}</dd>
                                    <dd><small>Ordered {formatDateLocal(this.state.surgery.orderedDate)}</small></dd>
                                </div>
                                <div>
                                    <dt>Marketer</dt>
                                    <dd>{(this.state.surgery.marketer && this.state.surgery.marketer.name && formatName(this.state.surgery.marketer.name)) || (<>Not Specified {this.props.authContext.hasRight("surgery:write:admin") ? <small>(Link to appointment to change)</small> : null}</>)}</dd>
                                    <dd>
                                        <small>Old: {(this.state.surgery.oldMarketer && this.state.surgery.oldMarketer.name && formatName(this.state.surgery.oldMarketer.name)) || "Not Specified"}
                                            <HasRight right="surgery:read:m">
                                                <span> ({this.state.surgery.bonus ? this.state.surgery.bonus.name : "Type Unset"})</span>
                                            </HasRight>
                                        </small>
                                    </dd>
                                </div>
                            </div>

                            <dt>Office visit</dt>
                            <dd>{this.officeVisitToString(this.state.surgery.officeVisitType)}</dd>
                            <dd>{(this.state.surgery.officeLocation && this.state.surgery.officeLocation.name) || "Not Specified"}</dd>
                            {
                                this.state.surgery.officeLocation
                                    ? <dd>
                                        <small>
                                            {this.state.surgery.officeLocation.address.street} {`${this.state.surgery.officeLocation.address.city}, ${this.state.surgery.officeLocation.address.state} ${this.state.surgery.officeLocation.address.zip}`}
                                        </small>
                                    </dd>
                                    : ""
                            }

                            <dt>Primary Operation</dt>
                            <dd>{(this.state.surgery.primarySurgery && this.state.surgery.primarySurgery.name) || "Unknown"}</dd>
                            {
                                this.state.surgery.primarySurgeryOptions.length > 0
                                    ? this.state.surgery.primarySurgeryOptions.map((a: any) => <dd key={a.id}><small>{a.name || a.id}</small></dd>)
                                    : ""
                            }

                            <dt>Additional Operations</dt>
                            {
                                this.state.surgery.additionalSurgeries && this.state.surgery.additionalSurgeries.length > 0
                                    ? this.state.surgery.additionalSurgeries.map((a: any, i: number) => <Fragment key={a.id}>
                                        <dd key={a.id}>{a.surgery.name}</dd>
                                        {
                                            a.options && a.options.length > 0
                                                ? a.options.map((b: any) => <dd key={b.id}><small>{b.name || b.id}</small></dd>)
                                                : ""
                                        }
                                    </Fragment>)
                                    : "None"
                            }
                        </dl>
                    </section>
                </div>
                &nbsp;
                <div className="column-root">
                    <div className="row-root row-root-auto">
                        <section className="info-block">
                            <header>
                                <h3>Staff</h3>
                            </header>
                            <dl className="info-list">
                                <div className="info-list-row">
                                    <div>
                                        <dt>Primary Surgeon</dt>
                                        <dd>{formatName(this.state.surgery.primarySurgeon.name)}</dd>
                                    </div>
                                    <div>
                                        <dt>Assisting Surgeon(s)</dt>
                                        {
                                            this.state.surgery.assistingSurgeons.length > 0
                                                ? this.state.surgery.assistingSurgeons.map((a: any) => <dd key={a.id}>{formatName(a.name)}</dd>)
                                                : <dd>None</dd>
                                        }
                                    </div>
                                </div>

                                <dt>Anesthetist</dt>
                                {
                                    this.state.surgery.anesthesiaProviders.length > 0
                                        ? this.state.surgery.anesthesiaProviders.map((a: any) => <dd key={a.id}>{formatName(a.name)}</dd>)
                                        : <dd>None</dd>
                                }

                                <div className="info-list-row">
                                    <div>
                                        <dt>Circulating Nurse(s)</dt>
                                        {
                                            this.state.surgery.circulatingNurses.length > 0
                                                ? this.state.surgery.circulatingNurses.map((a: any) => <dd key={a.id}>{formatName(a.name)}</dd>)
                                                : <dd>None</dd>
                                        }
                                    </div>
                                    <div>
                                        <dt>Scrub Nurse(s)</dt>
                                        {
                                            this.state.surgery.scrubNurses.length > 0
                                                ? this.state.surgery.scrubNurses.map((a: any) => <dd key={a.id}>{formatName(a.name)}</dd>)
                                                : <dd>None</dd>
                                        }
                                    </div>
                                </div>
                            </dl>
                        </section>
                        <section className="info-block">
                            <header>
                                <h3>Inventory</h3>
                            </header>

                            <dl className="info-list">
                                <dt>Counted</dt>
                                <div className="info-list-row">
                                    <div>
                                        <dd>
                                            <Checkbox fancy={true} id="frm-count-sponges" name="countedSponges" checked={this.state.surgery.countedSponges === true} indeterminate={this.state.surgery.countedSponges === null} disabled={true}>Sponges</Checkbox>
                                        </dd>
                                    </div>
                                    <div>
                                        <dd>
                                            <Checkbox fancy={true} id="frm-count-instruments" name="countedInstruments" checked={this.state.surgery.countedInstruments === true} indeterminate={this.state.surgery.countedInstruments === null} disabled={true}>Instruments</Checkbox>
                                        </dd>
                                    </div>
                                    <div>
                                        <dd>
                                            <Checkbox fancy={true} id="frm-count-needles" name="countedNeedles" checked={this.state.surgery.countedNeedles === true} indeterminate={this.state.surgery.countedNeedles === null} disabled={true}>Needles</Checkbox>
                                        </dd>
                                    </div>
                                </div>

                                <dt>Major Devices Used</dt>
                                {
                                    this.state.surgery.majorDevicesUsed.length > 0
                                        ? this.state.surgery.majorDevicesUsed.map((a: any) => (
                                            <dd key={a.name} className="flex-row-center-h pad-tiny-v">
                                                {a.count}&nbsp;&nbsp;&nbsp;
                                                <div>
                                                    <div>{a.name}</div>
                                                    <div className="small-text list-row-small">
                                                        {a.size ? <span className="small-text">Size: {a.size}</span> : ""}
                                                        {a.lot ? <span className="small-text">Lot: {a.lot}</span> : ""}
                                                        {a.serial ? <span className="small-text">Serial: {a.serial}</span> : ""}
                                                        {a.expiration ? <span className="small-text">Expiration: {a.expiration}</span> : ""}
                                                    </div>
                                                </div>
                                            </dd>
                                        ))
                                        : <dd>None</dd>
                                }

                                <dt>Drains & Packs</dt>
                                {
                                    this.state.surgery.drainsPacksUsed.length > 0
                                        ? this.state.surgery.drainsPacksUsed.map((a: any) => <dd key={a.name}>{a.name}</dd>)
                                        : <dd>None</dd>
                                }
                            </dl>
                        </section>
                    </div>
                    <section className="info-block">
                        <header>
                            <h3>Post-op Information</h3>
                        </header>
                        <dl className="info-list">
                            <div className="info-list-row">
                                <div>
                                    <dt>Anesthesia Start Time</dt>
                                    <dd>{this.state.surgery.anesthesiaStartTime ? formatTimeLocal(this.state.surgery.anesthesiaStartTime, true) : "Not specified"}</dd>
                                </div>
                                <div>
                                    <dt>Anesthesia End Time</dt>
                                    <dd>{this.state.surgery.anesthesiaEndTime ? formatTimeLocal(this.state.surgery.anesthesiaEndTime, true) : "Not specified"}</dd>
                                </div>
                            </div>

                            <div className="info-list-row">
                                <div>
                                    <dt>Operation Start Time</dt>
                                    <dd>{this.state.surgery.operationStartTime ? formatTimeLocal(this.state.surgery.operationStartTime, true) : "Not specified"}</dd>
                                </div>
                                <div>
                                    <dt>Operation End Time</dt>
                                    <dd>{this.state.surgery.operationEndTime ? formatTimeLocal(this.state.surgery.operationEndTime, true) : "Not specified"}</dd>
                                </div>
                            </div>

                            <dt>Anesthesia Type</dt>
                            <dd>{this.state.surgery.anesthesiaType}</dd>

                            <dt>Specimen(s)</dt>
                            {
                                this.state.surgery.specimens.length > 0
                                    ? this.state.surgery.specimens.map((a: any) => (
                                        <dd key={a.name} className="flex-row-center-h pad-tiny-v">
                                            <div>
                                                <div>{a.name}</div>
                                                <div className="small-text list-row-small">
                                                    {a.size ? <span className="small-text">Size: {a.size}</span> : ""}
                                                    {a.location ? <span className="small-text">Location: {a.location}</span> : ""}
                                                    {a.weight ? <span className="small-text">Weight: {a.weight}</span> : ""}
                                                </div>
                                            </div>
                                        </dd>
                                    ))
                                    : <dd>None</dd>
                            }

                            <div className="info-list-row">
                                <div>
                                    <dt>Post-op condition</dt>
                                    <dd>{this.state.surgery.conditionPost || "None"}</dd>
                                </div>
                                <div>
                                    <dt>Complications</dt>
                                    <dd>{this.state.surgery.complications || "None"}</dd>
                                </div>
                            </div>

                            <dt>Pre-op Diagnosis</dt>
                            <dd>{this.stringToElements(this.state.surgery.diagnosisPre) || "Unspecified"}</dd>

                            <dt>Post-op Diagnosis</dt>
                            <dd>{this.stringToElements(this.state.surgery.diagnosisPost) || "Unspecified"}</dd>
                        </dl>
                    </section>
                </div>
                &nbsp;
                <div className="column-root">
                    <div className="row-root  row-root-auto">
                        <section className="info-block">
                            <header>
                                <h3>Pre-Surgery Notes</h3>
                            </header>

                            {this.state.surgery.preSurgeryNotes.length === 0 ? "No notes" : ""}
                            <NoteBox notes={this.state.surgery.preSurgeryNotes} displayName={this.props.authContext.user.displayName} onNewNote={this.handlePreNote} hideControls={this.state.hidePreNoteControls} allowDeletion={this.props.authContext.hasRight("surgery:delete:note")} onNoteDelete={this.handlePreopNoteDelete} uploadFilter="image/jpeg,image/png,application/pdf" allowUpload={this.state.allowNoteUploads} />
                        </section>
                        {
                            this.props.authContext.hasRight("surgery:note:ins")
                                ? <section className="info-block">
                                    <header>
                                        <h3>Insurance Notes</h3>
                                    </header>

                                    {!this.state.surgery.insuranceNotes || this.state.surgery.insuranceNotes.length === 0 ? "No notes" : ""}
                                    <NoteBox notes={this.state.surgery.insuranceNotes} displayName={this.props.authContext.user.displayName} onNewNote={this.handleInsuranceNote} hideControls={this.state.hideInsuranceNoteControls} allowDeletion={this.props.authContext.hasRight("surgery:delete:note")} onNoteDelete={this.handleInsuranceNoteDelete} />
                                </section>
                                : undefined
                        }
                    </div>
                    <div className="row-root  row-root-auto">
                        <section className="info-block">
                            <header>
                                <h3>Post-Surgery Notes</h3>
                            </header>

                            {this.state.surgery.postSurgeryNotes.length === 0 ? "No notes" : ""}
                            <NoteBox notes={this.state.surgery.postSurgeryNotes} displayName={this.props.authContext.user.displayName} onNewNote={this.handlePostNote} hideControls={this.state.hidePostNoteControls} allowDeletion={this.props.authContext.hasRight("surgery:delete:note")} onNoteDelete={this.handlePostopNoteDelete} uploadFilter="image/jpeg,image/png,application/pdf" allowUpload={this.state.allowNoteUploads} />
                        </section>
                        <section className="info-block">
                            <header>
                                <h3>Marketing Notes</h3>
                            </header>

                            {!this.state.surgery.marketingNotes || this.state.surgery.marketingNotes.length === 0 ? "No notes" : ""}
                            <NoteBox notes={this.state.surgery.marketingNotes} displayName={this.props.authContext.user.displayName} onNewNote={this.handleMarketingNote} hideControls={this.state.hideMarketingNoteControls} allowDeletion={this.props.authContext.hasRight("surgery:delete:note")} onNoteDelete={this.handleMarketingNoteDelete} />
                        </section>
                        {
                            this.props.authContext.hasRight("surgery:note:contact")
                                ? <section className="info-block">
                                    <header>
                                        <h3>Contact Notes</h3>
                                    </header>

                                    {!this.state.surgery.contactNotes || this.state.surgery.contactNotes.length === 0 ? "No notes" : ""}
                                    <NoteBox notes={this.state.surgery.contactNotes} displayName={this.props.authContext.user.displayName} onNewNote={this.handleContactNote} hideControls={this.state.hideContactNoteControls} allowDeletion={this.props.authContext.hasRight("surgery:delete:note")} onNoteDelete={this.handleContactNoteDelete} />
                                </section>
                                : null
                        }
                    </div>
                </div>
                &nbsp;
                {
                    this.props.authContext.hasRight("surgery:history")
                        ? <div className="column-root">
                            <section className="info-block">
                                <header>
                                    <h3>History</h3>
                                </header>
                                <NoteBox notes={this.state.surgery.history} hideControls={true} />
                            </section>
                            <section className="block-spacer" />
                        </div>
                        : undefined
                }
            </div>
        );
    }

    private doMarkActive() {
        this.setState({
            isModalOpen: true,
            modalMode: "activate"
        });
    }

    private async doRealMarkActive(e: FormEvent) {
        e.preventDefault();

        try {
            this.setState({
                dialogSubmitStatus: true
            });

            await V2.patch(`surgery/${this.state.surgery.id}`, { time: this.state.dialogTime, status: 1 });

            this.setState({
                dialogSubmitStatus: false,
                isModalOpen: false,
            });

            await this.loadSurgery();
        }
        catch (e) {
            this.setState({
                dialogSubmitStatus: e.message,
            });
        }

    }

    private async doResetToActive(e: FormEvent) {
        e.preventDefault();

        try {
            await V2.patch(`surgery/${this.state.surgery.id}`, { status: 1 });
            await this.loadSurgery();
        }
        catch (e) {
            this.setState({
                loadError: e.message,
            });
        }
    }

    private async doResetToPending(e: FormEvent) {
        e.preventDefault();

        try {
            await V2.patch(`surgery/${this.state.surgery.id}`, { status: 0 });
            await this.loadSurgery();
        }
        catch (e) {
            this.setState({
                loadError: e.message,
            });
        }
    }

    private doCancel() {
        this.setState({
            isModalOpen: true,
            modalMode: "cancel"
        });
    }

    private async doMarkNoShow() {
        this.setState({
            isModalOpen: true,
            modalMode: "softcancel"
        });
    }

    private async doCancelNotNeeded() {
        this.setState({
            isModalOpen: true,
            modalMode: "notneeded"
        });
    }

    private async doOperationFailure() {
        this.setState({
            isModalOpen: true,
            modalMode: "failed"
        });
    }

    private async doRealCancel(e: FormEvent) {
        e.preventDefault();

        this.doRealCancelMode(2);
    }

    private async doRealSoftCancel(e: FormEvent) {
        e.preventDefault();

        this.doRealCancelMode(3);
    }

    private async doRealNotNeeded(e: FormEvent) {
        e.preventDefault();

        this.doRealCancelMode(5);
    }

    private async doRealFailure(e: FormEvent) {
        e.preventDefault();

        this.doRealCancelMode(6);
    }

    private async doRealCancelMode(status: number) {
        try {
            this.setState({
                dialogSubmitStatus: true
            });

            await V2.put(`surgery/${this.state.surgery.id}/status`, { status: status, reasonText: this.state.dialogReason });

            this.setState({
                dialogSubmitStatus: false,
                isModalOpen: false,
            });

            await this.loadSurgery();
        }
        catch (e) {
            this.setState({
                dialogSubmitStatus: e.message,
            });
        }
    }

    private closeDialog() {
        this.setState({
            isModalOpen: false,
            modalMode: "",

            dialogReason: "",
            dialogTime: "",
        });
    }

    private dialogInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLSelectElement>) {
        const name = e.currentTarget.name;
        const value: string | boolean | any = e.currentTarget.type === "checkbox" ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;


        if (e.currentTarget.type === "number" && !(e.currentTarget as any).step) {
            this.setState({
                [name]: parseInt(value, 10)
            } as any);
        }
        else {
            this.setState({
                [name]: value
            } as any);
        }
    }

    private cancelDialogReasonChanged(e: ChangeEvent<HTMLSelectElement>) {
        const name = e.currentTarget.name;
        const value = e.currentTarget.value;

        const reasonText = this.state.cancelReasons.find(a => a.title === value)?.value || "";

        this.setState({
            [name]: value,
            dialogReason: reasonText,
        } as any);
    }

    private async loadSurgery() {
        try {
            this.setState({
                isLoading: true,
            });

            const id = this.props.match.params.id;
            const surgery = await V2.get<any>(`surgery/${id}`);

            if (surgery) {
                // HACK: Assume time is specified in eastern time for now
                surgery.timestamp = (surgery.time
                    ? DateTime.fromISO(`${surgery.date.substring(0, 10)}T${surgery.time}`, { zone: "America/New_York" })
                    : DateTime.fromISO(`${surgery.date.substring(0, 10)}`, { zone: "America/New_York" })).toLocal();

                // Sort by time
                surgery.preSurgeryNotes && surgery.preSurgeryNotes.sort((a: any, b: any) => a.timestamp.localeCompare(b.timestamp));
                surgery.postSurgeryNotes && surgery.postSurgeryNotes.sort((a: any, b: any) => a.timestamp.localeCompare(b.timestamp));
                surgery.marketingNotes && surgery.marketingNotes.sort((a: any, b: any) => a.timestamp.localeCompare(b.timestamp));
                surgery.insuranceNotes && surgery.insuranceNotes.sort((a: any, b: any) => a.timestamp.localeCompare(b.timestamp));
                surgery.contactNotes && surgery.contactNotes.sort((a: any, b: any) => a.timestamp.localeCompare(b.timestamp));
                surgery.history && surgery.history.sort((a: any, b: any) => a.timestamp.localeCompare(b.timestamp));

                if (surgery.preSurgeryNotes) {
                    for (const note of surgery.preSurgeryNotes) {
                        note.author = formatName(note.employee.name);
                    }
                }

                if (surgery.postSurgeryNotes) {
                    for (const note of surgery.postSurgeryNotes) {
                        note.author = formatName(note.employee.name);
                    }
                }

                if (surgery.marketingNotes) {
                    for (const note of surgery.marketingNotes) {
                        note.author = formatName(note.employee.name);
                    }
                }

                if (surgery.insuranceNotes) {
                    for (const note of surgery.insuranceNotes) {
                        note.author = formatName(note.employee.name);
                    }
                }

                if (surgery.contactNotes) {
                    for (const note of surgery.contactNotes) {
                        note.author = formatName(note.employee.name);
                    }
                }

                if (surgery.primarySurgeryOptions) {
                    for (const opt of surgery.primarySurgeryOptions) {
                        const surgeryType = this.state.surgeries.find(a => a.id === surgery.primarySurgeryId);
                        if (surgeryType) {
                            const surgeryOpt = this.findSurgeryOption(surgeryType.options, opt.id);
                            if (surgeryOpt) {
                                opt.name = surgeryOpt.fullName || surgeryOpt.name;
                            }
                        }
                    }
                }

                if (surgery.additionalSurgeries) {
                    for (const operation of surgery.additionalSurgeries) {
                        for (const opt of operation.options) {
                            const surgeryType = this.state.surgeries.find(a => a.id === operation.id);
                            if (surgeryType) {
                                const surgeryOpt = this.findSurgeryOption(surgeryType.options, opt.id);
                                if (surgeryOpt) {
                                    opt.name = surgeryOpt.fullName || surgeryOpt.name;
                                }
                            }
                        }
                    }
                }

                if (surgery.history) {
                    surgery.history.sort((a: any, b: any) => a.timestamp.localeCompare(b.timestamp));
                    for (const item of surgery.history) {
                        item.author = formatName(item.employee.name);
                        item.body = this.renderHistoryBody(item);
                    }
                }
            }

            this.setState({
                isLoading: false,
                loadError: undefined,
                surgery,

                dialogTime: surgery.time
            });
        }
        catch (e) {
            this.setState({
                isLoading: false,
                loadError: e.message
            });
        }
    }

    private findSurgeryOption(options: any[], id: string): any {
        for (const item of options) {
            if (item.id === id) {
                return item;
            }

            if (item.children) {
                const childFound = this.findSurgeryOption(item.children, id);
                if (childFound) {
                    return childFound;
                }
            }
        }

        return undefined;
    }

    private sexToString(sex: string) {
        switch (sex) {
            case "f":
            case "F": return "Female";
            case "m":
            case "M": return "Male";
            default: return `Unknown (${sex})`;
        }
    }

    private appointmentTypeToString(type: number) {
        switch (type) {
            case 0: return "OR";
            case 1: return "Office";
            case 2: return "Indigent";
            default: return `Unknown: ${type}`;
        }
    }

    private stringToElements(text: string) {
        if (!text) {
            return null;
        }

        const paragraphs = text.replace(/\r/g, "").split(/\n/g);

        return <Fragment>
            {paragraphs.map((a, i) => <Fragment key={i}>{a}<br /></Fragment>)}
        </Fragment>
    }

    private async handlePreNote(body: string, files?: string[]) {
        // Send note to server
        const note = await V2.post<any>(`surgery/${this.props.match.params.id}/notes/preop`, { body, files });
        note.author = formatName(note.employee.name);

        this.setState(s => {
            s.surgery.preSurgeryNotes.push(note);

            return {
                hasNoteChanged: false,
                surgery: s.surgery,
            };
        });
    }

    private async handlePostNote(body: string, files?: string[]) {
        // Send note to server
        const note = await V2.post<any>(`surgery/${this.props.match.params.id}/notes/postop`, { body, files });
        note.author = formatName(note.employee.name);

        this.setState(s => {
            s.surgery.postSurgeryNotes.push(note);

            return {
                hasNoteChanged: false,
                surgery: s.surgery,
            };
        });
    }

    private async handleMarketingNote(body: string) {
        // Send note to server
        const note = await V2.post<any>(`surgery/${this.props.match.params.id}/notes/marketing`, { body });
        note.author = formatName(note.employee.name);

        this.setState(s => {
            s.surgery.marketingNotes.push(note);

            return {
                hasNoteChanged: false,
                surgery: s.surgery,
            };
        });
    }

    private async handleInsuranceNote(body: string) {
        // Send note to server
        const note = await V2.post<any>(`surgery/${this.props.match.params.id}/notes/insurance`, { body });
        note.author = formatName(note.employee.name);

        this.setState(s => {
            s.surgery.insuranceNotes.push(note);

            return {
                hasNoteChanged: false,
                surgery: s.surgery,
            };
        });
    }

    private async handleContactNote(body: string) {
        // Send note to server
        const note = await V2.post<any>(`surgery/${this.props.match.params.id}/notes/contact`, { body });
        note.author = formatName(note.employee.name);

        this.setState(s => {
            s.surgery.contactNotes.push(note);

            return {
                hasNoteChanged: false,
                surgery: s.surgery,
            };
        });
    }

    private async handlePreopNoteDelete(index: number) {
        // No try-catch because errors are handled by NoteBox
        const note = this.state.surgery.preSurgeryNotes[index];
        const unixTime = new Date(note.timestamp).getTime();

        await V2.delete(`surgery/${this.state.surgery.id}/notes/preop/${note.employeeId}/${unixTime}`);

        this.setState(s => {
            s.surgery.preSurgeryNotes.splice(index, 1);

            return {
                surgery: s.surgery
            }
        });
    }

    private async handlePostopNoteDelete(index: number) {
        // No try-catch because errors are handled by NoteBox
        const note = this.state.surgery.postSurgeryNotes[index];
        const unixTime = new Date(note.timestamp).getTime();

        await V2.delete(`surgery/${this.state.surgery.id}/notes/postop/${note.employeeId}/${unixTime}`);

        this.setState(s => {
            s.surgery.postSurgeryNotes.splice(index, 1);

            return {
                surgery: s.surgery
            }
        });
    }

    private async handleMarketingNoteDelete(index: number) {
        // No try-catch because errors are handled by NoteBox
        const note = this.state.surgery.marketingNotes[index];
        const unixTime = new Date(note.timestamp).getTime();

        await V2.delete(`surgery/${this.state.surgery.id}/notes/marketing/${note.employeeId}/${unixTime}`);

        this.setState(s => {
            s.surgery.marketingNotes.splice(index, 1);

            return {
                surgery: s.surgery
            }
        });
    }

    private async handleInsuranceNoteDelete(index: number) {
        // No try-catch because errors are handled by NoteBox
        const note = this.state.surgery.insuranceNotes[index];
        const unixTime = new Date(note.timestamp).getTime();

        await V2.delete(`surgery/${this.state.surgery.id}/notes/insurance/${note.employeeId}/${unixTime}`);

        this.setState(s => {
            s.surgery.insuranceNotes.splice(index, 1);

            return {
                surgery: s.surgery
            }
        });
    }

    private async handleContactNoteDelete(index: number) {
        // No try-catch because errors are handled by NoteBox
        const note = this.state.surgery.contactNotes[index];
        const unixTime = new Date(note.timestamp).getTime();

        await V2.delete(`surgery/${this.state.surgery.id}/notes/contact/${note.employeeId}/${unixTime}`);

        this.setState(s => {
            s.surgery.contactNotes.splice(index, 1);

            return {
                surgery: s.surgery
            }
        });
    }

    private deleteItem() {
        this.setState({
            isDeletionModalOpen: true
        });
    }

    private closeDeletionModal() {
        this.setState({
            isDeletionModalOpen: false
        });
    }

    private async realDeleteItem(e?: FormEvent) {
        if (e) {
            e.preventDefault();
        }

        try {
            this.setState({
                deletionModalLoading: true
            });

            await V2.delete(`surgery/${this.state.surgery.id}`);

            this.setState({
                deletionModalLoading: false,
                isDeletionModalOpen: false,
            });

            this.props.history.goBack();
        }
        catch (e) {
            this.setState({
                deletionModalLoading: e.message || e
            });
        }
    }

    private renderHistoryBody(evt: any) {
        switch (evt.type) {
            case 1: // Created
                return "Appointment created";
            case 2: // Updated
                return "Information updated";
            case 3: // Rescheduled
                return <Fragment>
                    <span>Rescheduled from <b>{formatDateLocal(evt.valueDate?.substring(0, 10))}</b> to <b>{formatDateLocal(evt.valueDate2?.substring(0, 10))}</b></span>
                    <br />
                    <br />
                    <span>
                        Reason:<br />
                        {evt.valueText || "No reason provided"}
                    </span>
                </Fragment>;
            case 4: // Status changed
                return <span>Changed status from <b>{surgeryStatusToText(evt.valueNumeric)}</b> to <b>{surgeryStatusToText(evt.valueNumeric2)}</b></span>;
            case 5: // Provider changed
                return <span>Changed provider from <b>{evt.valueNumeric ? formatName(this.lookupEmployee(evt.valueNumeric).name) : "None"}</b> to <b>{formatName(this.lookupEmployee(evt.valueNumeric2).name)}</b></span>;
            case 6: // Marketer changed
                return <span>Changed marketer from <b>{evt.valueNumeric ? formatName(this.lookupEmployee(evt.valueNumeric).name) : "None"}</b> to <b>{formatName(this.lookupEmployee(evt.valueNumeric2).name)}</b></span>;
            case 7: // Operation changed
                return "Operation(s) changed";
            case 8: // WCAS billing changed
                return "Clinic billing values changed";
            case 9: // Surgeon's fees changed
                return "Surgeon's fees values changed";
            case 10: // Review
                return <Fragment>Review: <b>{this.reviewStatusToString(evt.valueNumeric)}</b></Fragment>
            case 11: // Final review
                return <Fragment>Final review: <b>{this.reviewStatusToString(evt.valueNumeric)}</b></Fragment>
            default:
                return "Unknown history event type " + evt.type;
        }
    }

    private lookupEmployee(id: string) {
        return this.state.employees.find(a => a.id === id) || {
            name: { first: "Employee ", last: "#" + id }
        };
    }

    private getStrippedSurgery() {
        const stripped: any = {};
        Object.assign(stripped, this.state.surgery);

        delete stripped.history;

        return stripped;
    }

    private linkAppointment() {
        this.setState({
            isLinkModalOpen: true
        });
    }

    private closeLinkModal() {
        this.setState({
            isLinkModalOpen: false,
        });
    }

    private async realLinkAppointment(e: FormEvent) {
        e.preventDefault();

        try {
            this.setState({
                dialogSubmitStatus: true
            });

            await V2.patch(`surgery/${this.state.surgery.id}`, {
                officeAppointmentId: this.state.dialogLinkId
            });

            this.setState({
                dialogSubmitStatus: false,
                isLinkModalOpen: false,
            });

            await this.loadSurgery();
        }
        catch (e) {
            this.setState({
                dialogSubmitStatus: e.message || e || "Could not link appointment"
            });
        }
    }

    private openContactModal() {
        this.setState({
            isContactModalOpen: true
        });
    }

    private closeContactModal() {
        this.setState({
            isContactModalOpen: false
        });
    }

    private async contactStatus(status: string) {
        try {
            this.setState({
                contactModalLoading: true
            });

            let statusNumber = 0;
            switch (status) {
                case "will": statusNumber = 3; break;
                case "might": statusNumber = 2; break;
                case "not": statusNumber = 1; break;
                default: return;
            }

            const data = {
                isFinal: this.state.contactFinal,
                status: statusNumber,
                note: this.state.contactNote
            };

            await V2.post(`surgery/${this.state.surgery.id}/contact-status`, data);

            this.setState({
                contactModalLoading: false,
                isContactModalOpen: false,
                contactNote: "",
                contactFinal: false,
            }, () => this.props.history.goBack());
        }
        catch (e) {
            this.setState({
                contactModalLoading: e.message || e || "Could not set status"
            });
        }
    }

    private openReviewModal() {
        this.setState(s => {
            if (s.surgery.review && s.surgery.review.lastChecklist) {
                const checklist = s.surgery.review.lastChecklist;

                return {
                    isReviewModalOpen: true,

                    reviewChecklistBmi: checklist.bmi || false,
                    reviewChecklistBmiValue: checklist.bmiValue || "",
                    reviewChecklistHgb: checklist.hgb || false,
                    reviewChecklistHgbValue: checklist.hgbValue || "",
                    reviewChecklistA1c: checklist.a1c || false,
                    reviewChecklistA1cValue: checklist.a1cValue || "",
                    reviewChecklistPap: checklist.pap || false,
                    reviewChecklistPapValue: checklist.papValue || "",
                    reviewChecklistBp: checklist.bp || false,
                    reviewChecklistBpValue: checklist.bpValue || "",
                    reviewChecklistLabs: checklist.labs || false,
                    reviewChecklistLabsValue: checklist.labsValue || "",
                    reviewChecklistVitals: checklist.vitals || false,
                    reviewChecklistVitalsValue: checklist.vitalsValue || "",
                    reviewChecklistRx: checklist.rx || false,
                    reviewChecklistRxValue: checklist.rxValue || "",
                    reviewChecklistAllergies: checklist.allergies || false,
                    reviewChecklistAllergiesValue: checklist.allergiesValue || "",
                    reviewChecklistHealthHistory: checklist.healthHistory || false,
                    reviewChecklistHealthHistoryValue: checklist.healthHistoryValue || "",
                } as any;
            }
            else {
                return {
                    isReviewModalOpen: true
                };
            }
        });
    }

    private closeReviewModal() {
        if (this.state.reviewModalLoading !== true) {
            this.setState({
                isReviewModalOpen: false,
                reviewNoteExists: false,
                reviewNote: "",
                reviewChecklistBmi: false,
                reviewChecklistBmiValue: "",
                reviewChecklistHgb: false,
                reviewChecklistHgbValue: "",
                reviewChecklistA1c: false,
                reviewChecklistA1cValue: "",
                reviewChecklistPap: false,
                reviewChecklistPapValue: "",
                reviewChecklistBp: false,
                reviewChecklistBpValue: "",
                reviewChecklistLabs: false,
                reviewChecklistLabsValue: "",
                reviewChecklistVitals: false,
                reviewChecklistVitalsValue: "",
                reviewChecklistRx: false,
                reviewChecklistRxValue: "",
                reviewChecklistAllergies: false,
                reviewChecklistAllergiesValue: "",
                reviewChecklistHealthHistory: false,
                reviewChecklistHealthHistoryValue: "",
                reviewIsFinal: false,
            });
        }
    }

    private async reviewStatus(status: string) {
        if (!this.reviewNoteRef.current) {
            return;
        }

        try {
            this.setState({
                reviewModalLoading: true
            });

            let statusNumber = 0;
            switch (status) {
                case "goodtogo": statusNumber = 1; break;
                case "issue": statusNumber = 2; break;
                default: return;
            }

            // Process note
            if (this.reviewNoteRef.current) {
                await this.reviewNoteRef.current.submit();
            }

            const checklistUsed = Object.keys(this.state)
                .filter(a => a.startsWith("reviewChecklist"))
                .some(a => !!(this.state as any)[a]);

            const data = {
                status: statusNumber,
                note: this.state.reviewNote,
                attachments: this.state.reviewFiles,
                isFinal: this.state.reviewIsFinal,
                checklist: checklistUsed ? {
                    bmi: this.state.reviewChecklistBmi,
                    bmiValue: this.state.reviewChecklistBmiValue,
                    hgb: this.state.reviewChecklistHgb,
                    hgbValue: this.state.reviewChecklistHgbValue,
                    a1c: this.state.reviewChecklistA1c,
                    a1cValue: this.state.reviewChecklistA1cValue,
                    pap: this.state.reviewChecklistPap,
                    papValue: this.state.reviewChecklistPapValue,
                    bp: this.state.reviewChecklistBp,
                    bpValue: this.state.reviewChecklistBpValue,
                    labs: this.state.reviewChecklistLabs,
                    labsValue: this.state.reviewChecklistLabsValue,
                    vitals: this.state.reviewChecklistVitals,
                    vitalsValue: this.state.reviewChecklistVitalsValue,
                    rx: this.state.reviewChecklistRx,
                    rxValue: this.state.reviewChecklistRxValue,
                    allergies: this.state.reviewChecklistAllergies,
                    allergiesValue: this.state.reviewChecklistAllergiesValue,
                    healthHistory: this.state.reviewChecklistHealthHistory,
                    healthHistoryValue: this.state.reviewChecklistHealthHistoryValue,
                } : undefined
            };

            await V2.post(`surgery/${this.state.surgery.id}/review`, data);

            this.setState({
                reviewModalLoading: false,
                isReviewModalOpen: false,
                reviewNote: "",
            }, () => this.props.history.goBack());
        }
        catch (e) {
            this.setState({
                reviewModalLoading: e.message || e || "Could not set status"
            });
        }
    }

    private async handleReviewNote(body: string, files?: string[]) {
        this.setState({
            reviewNote: body,
            reviewFiles: files || []
        });
    }

    private handleReviewNoteChange(body: string) {
        if (!this.state.reviewNoteExists && body) {
            this.setState({
                reviewNoteExists: true
            });
        }
        else if (this.state.reviewNoteExists && !body) {
            this.setState({
                reviewNoteExists: false
            });
        }
    }

    private reviewStatusToString(status: number | string) {
        switch (status) {
            case 1:
            case "1":
                return "Good to go";
            case 2:
            case "2":
                return "Issue";
            default:
                return "";
        }
    }

    private officeVisitToString(type: string) {
        switch (type) {
            case "physical": return "Physical visit";
            case "telemed": return "Telemedicine visit";
            default: return type;
        }
    }

    private modalModeToSubmit(modalMode: string) {
        switch (modalMode) {
            case "cancel": return this.doRealCancel;
            case "notneeded": return this.doRealNotNeeded;
            case "softcancel": return this.doRealSoftCancel;
            case "failed": return this.doRealFailure;
            default: return () => { };
        }
    }

    private modalModeToTitle(modalMode: string) {
        switch (modalMode) {
            case "cancel": return "Hard-cancel Appointment";
            case "notneeded": return "Cancel unneeded telemed appointment";
            case "softcancel": return "Soft-cancel Appointment";
            case "failed": return "Failed operation";
            default: return modalMode;
        }
    }

    private modalModeToPhrase(modalMode: string) {
        switch (modalMode) {
            case "cancel": return "Please enter the reason for hard-canceling the appointment.";
            case "notneeded": return "Please enter the reason for canceling the appointment.";
            case "softcancel": return "Please enter the reason for soft-canceling the appointment.";
            case "failed": return "Please enter the reason this operation is considered failed.";
            default: return modalMode;
        }
    }

    private openLabelSheetWindow() {
        window.open("/print/surgery/label-sheet", "BOOK_WINDOW", "resizable,scrollbars");
    }

    private handleMessage(e: MessageEvent) {
        if (!e.source || (e.source instanceof MessagePort) || (e.source instanceof ServiceWorker)) {
            return;
        }

        if (e.data.action && e.data.action === "surgery_label_sheet_request") {
            const originator = e.source;
            this.loadLabelSheetData()
                .then(rows => {
                    // Popup is asking for parameters
                    const message = {
                        action: "surgery_label_sheet_parameters",
                        labels: rows
                    };

                    originator.postMessage(message, "*");
                })
                .catch(e => {
                    // Send error to page too
                    originator.postMessage({
                        action: "surgery_label_sheet_error",
                        error: e.message || "Could not load label sheet data"
                    }, "*");
                });
        }
    }

    private async loadLabelSheetData() {
        // As of 2022-05-23, 1000 is the resource number for the label sheet layout
        const [resource, documentTypesResult] = await Promise.all([
            V2.get<any>("resources/1000"),
            V2.get<any>("document/types?show_all=true&limit=100")
        ]);

        // Convert to a map for lookups
        const documentTypeLookup: { [id: string]: any } = {};
        for (const document of documentTypesResult.documentTypes) {
            documentTypeLookup[document.id] = document;
        }

        const outputRows = [];

        for (const row of resource.rows) {
            const newRow = [];
            for (const label of row) {
                if (label.mode === "address") {
                    newRow.push({
                        name: this.state.surgery.patient.name,
                        birthDate: this.state.surgery.patient.birthDate,
                        chartNumber: this.state.surgery.patient.chartNumber,
                        mode: "address",
                        address: this.state.surgery.patient.address,
                    });

                    continue;
                }

                let document = documentTypeLookup[label.typeId];
                if (!document) {
                    // As of 2022-05-23, 1026 is the generic 'Imported Document' document type ID
                    document = documentTypeLookup["1026"];
                }

                newRow.push({
                    barcodeData: this.generateBarcodeData(
                        this.state.surgery.patient.chartNumber,
                        document.id,
                        document.latestVersion.version,
                        this.state.surgery.id),
                    documentName: document.name,
                    name: this.state.surgery.patient.name,
                    birthDate: this.state.surgery.patient.birthDate,
                    chartNumber: this.state.surgery.patient.chartNumber,
                });
            }

            outputRows.push(newRow);
        }

        return outputRows;
    }

    private generateBarcodeData(chartNumber: string, documentTypeId: string, documentTypeVersion: number, surgeryId?: string) {
        if (surgeryId) {
            return `$1:${chartNumber}:${documentTypeId}:${documentTypeVersion}:${surgeryId}`;
        }

        return `$1:${chartNumber}:${documentTypeId}:${documentTypeVersion}`;
    }
}

export default withTitle("Surgery Appointment Details", withAuth(SurgeryAppointmentView));
